import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Empty, Kpi, KpiGrid, Muted, PageHeader, Section } from '@/components/ui'
import { syncRhidCadastro, type SyncRhidResult } from './api'
import { ImportPipelineNav } from './ImportPipelineNav'

export function ImportarRhidPage() {
  const { canEdit } = useAuth()
  const editable = canEdit('colaboradores')
  const qc = useQueryClient()

  const [somenteCadastro, setSomenteCadastro] = useState(true)
  const [atualizarCadastro, setAtualizarCadastro] = useState(true)
  const [dias, setDias] = useState(30)
  const [msg, setMsg] = useState('')
  const [erro, setErro] = useState(false)
  const [pending, setPending] = useState(false)
  const [resultado, setResultado] = useState<SyncRhidResult | null>(null)

  async function executar() {
    setPending(true)
    setErro(false)
    setMsg(
      somenteCadastro
        ? 'Sincronizando cadastro RHID (vínculo + CPF/matrícula)…'
        : 'Sincronizando RHID (cadastro + espelho de ponto)…',
    )
    setResultado(null)
    try {
      const res = await syncRhidCadastro({
        dias,
        somenteCadastro,
        atualizarCadastro,
      })
      setResultado(res)
      const v = res.vinculo
      setMsg(
        res.sucesso === false
          ? res.mensagem || 'Falha no sync RHID.'
          : `RHID concluído. ${v?.vinculados ?? 0} vínculos novos · ${v?.cadastro_atualizado ?? 0} cadastros atualizados · ${v?.sem_match ?? 0} sem match.`,
      )
      setErro(res.sucesso === false)
      if (res.sucesso !== false) {
        await qc.invalidateQueries({ queryKey: ['rh', 'colaboradores'] })
        await qc.invalidateQueries({ queryKey: ['rh', 'kpis'] })
      }
    } catch (err) {
      setResultado(null)
      setErro(true)
      setMsg(err instanceof ApiError ? err.message : 'Falha na sincronização RHID.')
    } finally {
      setPending(false)
    }
  }

  if (!editable) {
    return (
      <section className="hi-page">
        <PageHeader title="Importar RHID" subtitle="Acesso restrito a RH/Admin." />
        <Alert tone="danger">Sem permissão para sincronizar RHID.</Alert>
      </section>
    )
  }

  const v = resultado?.vinculo
  const detalhes = v?.detalhes || []

  return (
    <section className="hi-page hi-import-colab-page">
      <PageHeader
        title="Importar RHID"
        subtitle="Após o Domínio: vincula pessoas do ponto (id_rhid) e completa CPF, matrícula, PIS, nome e admissão. RHID é a fonte da verdade desses campos."
        actions={
          <Link className="btn btn-secondary" to="/app/rh">
            Voltar à lista
          </Link>
        }
      />

      <ImportPipelineNav active="rhid" />

      {msg ? <Alert tone={erro ? 'danger' : 'info'}>{msg}</Alert> : null}

      <Section title="Sincronizar">
        <form
          className="hi-filters hi-import-form"
          onSubmit={(e) => {
            e.preventDefault()
            void executar()
          }}
        >
          <label className="hi-check">
            <input
              type="checkbox"
              checked={somenteCadastro}
              disabled={pending}
              onChange={(e) => setSomenteCadastro(e.target.checked)}
            />
            Só cadastro (sem espelho de ponto)
          </label>
          <label className="hi-check">
            <input
              type="checkbox"
              checked={atualizarCadastro}
              disabled={pending}
              onChange={(e) => setAtualizarCadastro(e.target.checked)}
            />
            Atualizar CPF / matrícula / nome / PIS
          </label>
          {!somenteCadastro ? (
            <label>
              Dias de ponto
              <input
                type="number"
                min={1}
                max={90}
                value={dias}
                disabled={pending}
                onChange={(e) => setDias(Number(e.target.value) || 30)}
                aria-label="Dias de ponto"
              />
            </label>
          ) : null}
          <button type="submit" className="btn btn-primary" disabled={pending}>
            {pending ? 'Sincronizando…' : 'Sincronizar RHID'}
          </button>
        </form>
        <Muted>
          Match: CPF → e-mail → id_rhid → matrícula → nome. Quem não casar fica em “sem match”
          (não cria colaborador novo). Depois rode o Bitrix para e-mail corporativo e foto.
        </Muted>
      </Section>

      {resultado?.sucesso !== false && v ? (
        <>
          <KpiGrid>
            <Kpi label="Pessoas RHID" value={String(v.pessoas_rhid ?? 0)} />
            <Kpi label="Vinculados" value={String(v.vinculados ?? 0)} />
            <Kpi label="Cadastro atualizado" value={String(v.cadastro_atualizado ?? 0)} />
            <Kpi label="Já ok" value={String(v.ja_vinculados ?? 0)} />
            <Kpi label="Sem match" value={String(v.sem_match ?? 0)} />
            {!resultado.somente_cadastro ? (
              <Kpi label="Ponto sync" value={String(resultado.sincronizados ?? 0)} />
            ) : null}
          </KpiGrid>

          <Section title="Detalhes" flush>
            {!detalhes.length ? <Empty>Sem alterações de cadastro nesta rodada.</Empty> : null}
            {detalhes.length > 0 ? (
              <div className="hi-import-detalhes">
                <Muted>Atualizações (amostra)</Muted>
                <ul>
                  {detalhes.slice(0, 40).map((d, i) => (
                    <li key={`rhid-${i}`}>
                      <strong>{d.nome}</strong>
                      {d.id_rhid != null ? ` · RHID ${d.id_rhid}` : ''}
                      {d.match ? ` (${d.match})` : ''}
                      {d.campos?.length ? ` — ${d.campos.join(', ')}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {(resultado.erros?.length ?? 0) > 0 ? (
              <div className="hi-import-detalhes">
                <Muted>Erros de ponto</Muted>
                <ul>
                  {resultado.erros!.slice(0, 20).map((e, i) => (
                    <li key={`err-${i}`}>
                      {e.colaborador_id}: {e.erro}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Section>

          <Section title="Próximo passo">
            <Link className="btn btn-primary" to="/app/rh/importar/bitrix">
              Ir para Bitrix
            </Link>
          </Section>
        </>
      ) : null}
    </section>
  )
}
